import { Download } from "lucide-react";
import { downloadCsv, formatDate } from "@/lib/adminApi";

/**
 * Generic read-only table for a dataset. `columns` = [{ key, label, render? }].
 */
export function DataTable({ dataset, title, columns, rows, loading, actions, actionsLabel = "Actions" }) {
  return (
    <div className="rounded-2xl border border-line bg-white shadow-sm" data-testid={`admin-table-${dataset}`}>
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-line px-6 py-4">
        <div>
          <h2 className="font-poster text-lg font-bold text-steel">{title}</h2>
          <p className="font-mono-sub text-[11px] text-mute">
            {loading ? "Loading…" : `${rows.length} record${rows.length === 1 ? "" : "s"}`}
          </p>
        </div>
        <button
          type="button"
          data-testid={`admin-export-${dataset}`}
          onClick={() => downloadCsv(dataset)}
          className="inline-flex items-center gap-2 rounded border border-slate-300 px-4 py-2 font-mono-sub text-xs font-bold uppercase tracking-wider text-steel transition-colors hover:bg-cloud"
        >
          <Download className="h-3.5 w-3.5" aria-hidden="true" /> Export CSV
        </button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-left text-sm">
          <thead className="bg-cloud font-mono-sub text-[11px] uppercase tracking-wider text-mute">
            <tr>
              <th className="px-6 py-3 font-semibold">Received</th>
              {columns.map((c) => (
                <th key={c.key} className="px-4 py-3 font-semibold">{c.label}</th>
              ))}
              {actions && <th className="px-4 py-3 font-semibold text-right">{actionsLabel}</th>}
            </tr>
          </thead>
          <tbody className="divide-y divide-line">
            {!loading && rows.length === 0 && (
              <tr>
                <td colSpan={columns.length + 2} className="px-6 py-10 text-center text-sm text-mute" data-testid={`admin-empty-${dataset}`}>
                  Nothing here yet.
                </td>
              </tr>
            )}
            {rows.map((row) => (
              <tr key={row.id} data-testid={`admin-row-${row.id}`} className="align-top hover:bg-cloud/60">
                <td className="whitespace-nowrap px-6 py-3 font-mono-sub text-xs text-mute">{formatDate(row.created_at)}</td>
                {columns.map((c) => (
                  <td key={c.key} className="max-w-xs px-4 py-3 text-ink">
                    {c.render ? c.render(row) : row[c.key] || "—"}
                  </td>
                ))}
                {actions && <td className="px-4 py-3 text-right">{actions(row)}</td>}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
